import { useEffect, useState } from "react"
import { useLocation } from "react-router"
import { CheckIcon, SendIcon, ThumbsDownIcon, ThumbsUpIcon } from "lucide-react"
import { captureEvent } from "~/client/posthog.client"
import { cn } from "~/lib/utils"

// Per-page inline feedback widget. Captures two discrete PostHog events:
//   - docs_feedback           { pathname, helpful: true|false }
//   - docs_feedback_comment   { pathname, comment }     (only on negative votes)
// Analyses in PostHog can group by pathname, filter helpful=true/false, or
// pull comments by querying events of type docs_feedback_comment.
//
// State is in-memory only — the "thanks" confirmation auto-clears after a
// few seconds, returning to the prompt. We deliberately don't persist anything
// across reloads or navigations: stale state on a doc that has since been
// rewritten would suppress fresh, more useful feedback.
type State = "idle" | "voted_down" | "thanks"

const THANKS_DURATION_MS = 3500

export function PageFeedback() {
  const { pathname } = useLocation()
  const [state, setState] = useState<State>("idle")
  const [comment, setComment] = useState("")

  // Reset on route change.
  useEffect(() => {
    setState("idle")
    setComment("")
  }, [pathname])

  // Auto-clear the "thanks" confirmation after a few seconds so the prompt
  // reappears — readers who change their mind can re-engage without a reload.
  useEffect(() => {
    if (state !== "thanks") return
    const id = window.setTimeout(() => setState("idle"), THANKS_DURATION_MS)
    return () => window.clearTimeout(id)
  }, [state])

  const handleVote = (helpful: boolean) => {
    captureEvent("docs_feedback", { pathname, helpful })
    setState(helpful ? "thanks" : "voted_down")
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = comment.trim()
    if (trimmed.length === 0) return
    captureEvent("docs_feedback_comment", { pathname, comment: trimmed })
    setComment("")
    setState("thanks")
  }

  return (
    <div className="mt-16 border-t pt-8">
      {state === "thanks" ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <CheckIcon className="h-4 w-4 text-primary" />
          Thanks for the feedback.
        </div>
      ) : (
        <>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm font-medium text-foreground">
              Did you find what you were looking for?
            </span>
            <div className="flex items-center gap-2">
              <VoteButton
                label="Yes, this was helpful"
                active={false}
                onClick={() => handleVote(true)}
                icon={ThumbsUpIcon}
              />
              <VoteButton
                label="No, this didn't help"
                active={state === "voted_down"}
                onClick={() => handleVote(false)}
                icon={ThumbsDownIcon}
              />
            </div>
          </div>

          {state === "voted_down" && (
            <form onSubmit={handleSubmit} className="mt-4 space-y-2">
              <label
                htmlFor="docs-feedback-comment"
                className="block text-xs text-muted-foreground"
              >
                Tell us what was missing — we'll use it to improve this page.
              </label>
              <textarea
                id="docs-feedback-comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={3}
                placeholder="What were you looking for?"
                className="w-full max-w-2xl px-3 py-2 text-sm border rounded-md bg-background outline-none focus:border-primary resize-y"
              />
              <div className="flex items-center gap-2">
                <button
                  type="submit"
                  disabled={comment.trim().length === 0}
                  className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <SendIcon className="h-3 w-3" />
                  Send feedback
                </button>
                <button
                  type="button"
                  onClick={() => setState("idle")}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </>
      )}
    </div>
  )
}

function VoteButton({
  label,
  active,
  onClick,
  icon: Icon,
}: {
  label: string
  active: boolean
  onClick: () => void
  icon: typeof ThumbsUpIcon
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={cn(
        "inline-flex h-8 w-8 items-center justify-center rounded-md border transition-colors",
        active
          ? "bg-primary text-primary-foreground border-primary"
          : "bg-background text-muted-foreground hover:bg-muted hover:text-foreground",
      )}
    >
      <Icon className="h-4 w-4" />
    </button>
  )
}
